import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { mixpanelTrack, gainsightTrack } from 'utils';
import { useGetReports, useGetReportsStatus } from '@queries/core/reports';
import { selectToken } from '@redux/slices/userSlice';
import { Report, ReportStatus, ReportToStatus, ReportsSources, coreService } from 'network-utils';
import { translate } from 'translations';
import DownloadHubListItems from '../list/downloadHubList';
import DownloadhubExtended from '../rightPanel/downloadhubExtended';
import { DownloadHubButtonStatus } from '../downloadHubButton';

const DownloadHubContainer: FC<DownloadHubContainerProps> = (props: DownloadHubContainerProps) => {
    const {
        isPopup,
        setIsDownloadHubOpen,
        onShowDownloadHubExtended,
        menuRef,
        selectTimeFrameRef,
        handleDownloadHubStatus,
        reportStatusData
    } = props;
    const POLLING_INTERVAL = 5000;
    const DELETION_MINUTES_ADDITIONS = 5;
    const UNDO_LIMIT = 1;
    const LIMIT = 8;
    const EXTENDED_LIMIT = 20;
    const UPDATE_UNDO_INTERVAL = 10000;
    const REPORT_DELETED = translate('ReportDeleted');
    const REPORT_FAILED_TO_DELETE = translate('ReportFailedToDelete');
    const REPORT_RENAMED = translate('ReportRenamed');
    const UNDO = translate('Undo');
    const RETRY = translate('Retry');

    const [reportsErrors, setReportsErrors] = useState<object>({});
    const [search, setSearch] = useState<undefined | string>();
    const [page, setPage] = useState<number>(0);
    const [sort, setSort] = useState<boolean>(true);
    const [timeframeFilter, setTimeframeFilter] = useState<any>({});
    const [greenCheckUuids, setGreenCheckUuids] = useState<string[]>([]);

    const setSearchAction = (val: string) => {
        if (val.length) {
            setSearch(val);
        }
    };

    const sourceFilter = {
        source: [
            ReportsSources.GENERATE_ACTIVITY_CSV_REPORT,
            ReportsSources.GENERATE_INVITATIONS_DRAFTS_USERS_ERRORS_CSV,
            ReportsSources.GENERATE_ASSIGNED_CONTENT_PROGRESS_CSV
        ]
    };

    const getReportsOptions = {
        search,
        offset: page * EXTENDED_LIMIT,
        limit: EXTENDED_LIMIT,
        sort: sort ? 'asc' : 'desc',
        ...timeframeFilter,
        ...sourceFilter
    };

    const [limitedReports, setLimitedReports] = useState<Report[]>([]);
    const [reports, setReports] = useState<Report[]>([]);
    const [total, setTotal] = useState<number>(0);

    const [undoData, setUndoData] = useState<any>({});

    useEffect(() => {
        if (!isPopup) {
            mixpanelTrack({
                eventName: 'Download Hub See All'
            });
            gainsightTrack('Download Hub See All', {});
        }
    }, [isPopup]);

    const token = useSelector(selectToken);
    const { data: limitedReportsData, isLoading: isLimitedLoading } = useGetReports(token, isPopup, {
        limit: LIMIT,
        ...sourceFilter
    });
    const { data: reportsData, isLoading, isFetching } = useGetReports(token, !isPopup, getReportsOptions);

    const { data: alternativeReportStatusData } = useGetReportsStatus(
        token,
        Boolean(reports.length && !reportStatusData),
        POLLING_INTERVAL
    );

    const relevantReportsStatusData = reportStatusData || alternativeReportStatusData?.reports;

    const updateUndoData = () => {
        const currentDate = new Date();
        const undoDataCpy = { ...undoData };
        let isChanged = false;
        Object.keys(undoDataCpy).map(key => {
            if (undoDataCpy[key].undoUntil <= currentDate.getTime()) {
                delete undoDataCpy[key];
                isChanged = true;
            }
        });
        isChanged && setUndoData(undoDataCpy);
    };

    useEffect(() => {
        if (Object.keys(undoData).length) {
            const interval = setInterval(() => {
                updateUndoData();
            }, UPDATE_UNDO_INTERVAL);
            return () => clearInterval(interval);
        }
        return undefined;
    }, [undoData]);

    useEffect(() => {
        setPage(0);
    }, [search, timeframeFilter]);

    const updateReportsStatus = (relevantReports: Report[], setCallback: Function, reportsStatus: ReportToStatus[]) => {
        const relevantReportsCpy = [...relevantReports];
        reportsStatus.forEach(reportStatus => {
            const report = relevantReportsCpy.find(report => report.uuid == reportStatus.uuid);
            if (report) {
                if (report.status == ReportStatus.IN_PROGRESS && reportStatus.status == ReportStatus.COMPLETED) {
                    setGreenCheckUuids([...greenCheckUuids, report.uuid]);
                }
                report.status = reportStatus.status;
            }
        });
        setCallback(relevantReports);
    };

    useEffect(() => {
        if (limitedReportsData && limitedReportsData.success) {
            setLimitedReports(limitedReportsData.reports);
        }
    }, [limitedReportsData]);

    useEffect(() => {
        if (reportsData && reportsData.success) {
            setReports(reportsData.reports);
            setTotal(reportsData.total);
        }
    }, [reportsData]);

    useEffect(() => {
        if (relevantReportsStatusData && relevantReportsStatusData.length) {
            reports.length && updateReportsStatus(reports, setReports, relevantReportsStatusData);
            limitedReports.length && updateReportsStatus(limitedReports, setLimitedReports, relevantReportsStatusData);
        }
    }, [relevantReportsStatusData, reports, limitedReports]);

    const handleError = (err: Error, uuid: string): void => {
        const reportsErrorsCpy: any = { ...reportsErrors };
        reportsErrorsCpy[uuid] = err;
        setReportsErrors(reportsErrorsCpy);
    };

    const updateSingleReport = (
        relevantReports: any,
        setCallback: Function,
        uuid: string,
        value: string | boolean,
        key: string
    ) => {
        const relevantReportsCpy = [...relevantReports];
        const report = relevantReportsCpy.find(report => report.uuid == uuid);
        if (report) {
            report[key] = value;
        }
        setCallback(relevantReports);
    };

    const handleResponse = (uuid: string, res: any, value: string | boolean, key: string) => {
        if (res && res.success) {
            reports.length && updateSingleReport(reports, setReports, uuid, value, key);
            limitedReports.length && updateSingleReport(limitedReports, setLimitedReports, uuid, value, key);
        }
    };

    const onUndo = (callback: Function, uuid: string) => {
        const undoDataCpy = { ...undoData };
        delete undoDataCpy[uuid];
        setUndoData(undoDataCpy);
        callback();
    };

    const handleRename = (uuid: string, name: string, formerName?: string) => {
        const currentDate = new Date();
        const undoUntil = new Date().setMinutes(currentDate.getMinutes() + UNDO_LIMIT);

        coreService.reportsApi
            .updateReportName(token, uuid, name)
            .then((res: any) => {
                formerName &&
                    setUndoData({
                        ...undoData,
                        [uuid]: {
                            title: REPORT_RENAMED,
                            callbackTitle: UNDO,
                            isUndo: true,
                            callback: () => onUndo(() => handleRename(uuid, formerName), uuid),
                            undoUntil
                        }
                    });
                handleResponse(uuid, res, res.updatedName, 'name');
            })
            .catch(e => handleError(e, uuid));
    };

    const undoDelete = async (uuid: string, status: ReportStatus) => {
        await coreService.reportsApi.undoDelete(token, uuid, status);
    };

    const handleDelete = (uuid: string, status: ReportStatus) => {
        const currentDate = new Date();
        const undoUntil = new Date().setMinutes(currentDate.getMinutes() + UNDO_LIMIT);
        currentDate.setMinutes(currentDate.getMinutes() + DELETION_MINUTES_ADDITIONS);

        coreService.reportsApi
            .deleteReport(token, uuid, currentDate.toUTCString())
            .then((res: any) => {
                setUndoData({
                    ...undoData,
                    [uuid]: {
                        title: REPORT_DELETED,
                        callbackTitle: UNDO,
                        callback: () => onUndo(() => undoDelete(uuid, status), uuid),
                        undoUntil
                    }
                });
                handleResponse(uuid, res, ReportStatus.DELETING, 'status');
            })
            .catch(e => {
                setUndoData({
                    ...undoData,
                    [uuid]: {
                        title: REPORT_FAILED_TO_DELETE,
                        callbackTitle: RETRY,
                        callback: () => onUndo(() => handleDelete(uuid, status), uuid),
                        undoUntil
                    }
                });
                handleError(e, uuid);
            });
    };

    const handleDownload = async (uuid: string, source: string) => {
        const result = await coreService.reportsApi.getCsvUrl(token, uuid, source);
        window.open(result.signedUrl, '_blank', 'noreferrer');
    };

    const handleViewed = (uuid: string) => {
        coreService.reportsApi
            .updateReportViewed(token, uuid)
            .then((res: any) => {
                handleResponse(uuid, res, true, 'isViewed');
            })
            .catch(e => handleError(e, uuid));
    };

    const handleDateFilter = (data: any) => {
        const timeframeFilter: any = {};
        timeframeFilter.fromDate = data.from;
        timeframeFilter.toDate = data.to;
        setTimeframeFilter(timeframeFilter);
    };

    const handleRegenerate = (uuid: string) => {
        handleDownloadHubStatus?.(DownloadHubButtonStatus.inProgress);
        coreService.reportsApi
            .reExportCsv(token, uuid)
            .then((res: any) => {
                handleResponse(uuid, res, ReportStatus.IN_PROGRESS, 'status');
            })
            .catch(e => handleError(e, uuid));
    };

    const filteredReports = (relevantReports: Report[]) => {
        return relevantReports.filter(report => report.status !== ReportStatus.DELETING);
    };

    const handleGreenCheckOff = (uuid: string) => {
        const greenCheckUuidsCpy = [...greenCheckUuids];
        greenCheckUuidsCpy.splice(greenCheckUuidsCpy.indexOf(uuid), 1);
        setGreenCheckUuids([...greenCheckUuidsCpy]);
    };

    return (
        <div>
            {isPopup && (
                <DownloadHubListItems
                    onClose={() => setIsDownloadHubOpen?.(false)}
                    handleDownload={handleDownload}
                    rows={filteredReports(limitedReports)}
                    onSeeAll={onShowDownloadHubExtended}
                    handleRename={handleRename}
                    handleDelete={handleDelete}
                    handleViewed={handleViewed}
                    onRegenerate={handleRegenerate}
                    undoData={undoData}
                    greenCheckUuids={greenCheckUuids}
                    handleGreenCheckOff={handleGreenCheckOff}
                    isLoading={isLimitedLoading}
                    totalRows={total}
                />
            )}
            {!isPopup && (
                <DownloadhubExtended
                    selectTimeFrameRef={selectTimeFrameRef}
                    menuRef={menuRef}
                    data={filteredReports(reports)}
                    onSearch={(val: string) => setSearchAction(val)}
                    totalRows={filteredReports(reports).length}
                    isLoading={isLoading}
                    isFetching={isFetching}
                    fetchNextPage={() => setPage(page + 1)}
                    searchString={search || ''}
                    setSelectedIds={() => undefined}
                    onDownload={handleDownload}
                    setSort={() => setSort(!sort)}
                    handleViewed={handleViewed}
                    onRegenerate={handleRegenerate}
                    handleDateFilter={handleDateFilter}
                    onDelete={handleDelete}
                    undoData={undoData}
                    handleRename={handleRename}
                    greenCheckUuids={greenCheckUuids}
                    handleGreenCheckOff={handleGreenCheckOff}
                />
            )}
        </div>
    );
};

export default DownloadHubContainer;

interface DownloadHubContainerProps {
    isPopup: boolean;
    setIsDownloadHubOpen?: Function;
    onShowDownloadHubExtended?: Function;
    menuRef?: any;
    selectTimeFrameRef?: any;
    handleDownloadHubStatus?: Function;
    reportStatusData?: ReportToStatus[];
}
